import { Button } from '@1trading/react/components/ui/button';
import { cn } from '@1trading/react/lib/utils';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { ArrowLeftIcon, ChevronLeft } from 'lucide-react';
import React, { createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';

const pageHeadingVariants = cva('w-full mb-8 relative', {
  variants: {
    centered: {
      true: 'space-y-4 text-center',
      false: 'space-y-3',
    },
  },
  defaultVariants: {
    centered: false,
  },
});

type PageHeadingVariants = VariantProps<typeof pageHeadingVariants>;

const PageHeadingContext = createContext<Partial<Pick<PageHeadingVariants, 'centered'>>>({ centered: false });

export interface PageHeadingProps extends React.HTMLAttributes<HTMLElement>, PageHeadingVariants {
  asChild?: boolean;
}

export const PageHeading = React.forwardRef<HTMLLegendElement, PageHeadingProps>(({ children, className, asChild, centered, ...props }, ref) => {
  const Comp = asChild ? Slot : 'header';

  return (
    <PageHeadingContext.Provider value={{ centered }}>
      <Comp ref={ref} className={cn(pageHeadingVariants({ centered, className }))} {...props}>
        {children}
      </Comp>
    </PageHeadingContext.Provider>
  );
});
PageHeading.displayName = 'PageHeading';

const descriptionVariants = cva('text-base', {
  variants: {
    centered: {
      true: '',
      false: 'md:pr-[20%]',
    },
  },
  defaultVariants: {
    centered: false,
  },
});

export const PageHeadingDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(({ children, className, ...props }, ref) => {
  const { centered } = useContext(PageHeadingContext);

  return (
    <p ref={ref} className={cn(descriptionVariants({ centered, className }))} {...props}>
      {children}
    </p>
  );
});
PageHeadingDescription.displayName = 'PageHeadingDescription';

export const PageHeadingTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement> & { asChild?: boolean }>(
  ({ asChild, children, className, ...props }, ref) => {
    const Comp = asChild ? Slot : 'h1';

    return (
      <Comp ref={ref} className={cn('text-2xl md:text-4xl font-semibold leading-tight md:leading-none', className)} {...props}>
        {children}
      </Comp>
    );
  },
);
PageHeadingTitle.displayName = 'PageHeadingTitle';

const pageHeadingButtonVariants = cva('gap-2 mb-4', {
  variants: {
    floatingInPage: {
      left: 'lg-xl:absolute lg-xl:top-0 lg-xl:right-full lg-xl:mr-8 xl:!mr-24 2xl:!mr-48',
      right: 'lg-xl:absolute lg-xl:top-0 lg-xl:left-full lg-xl:ml-8 xl:!ml-24 2xl:!ml-48',
      false: '',
    },
  },
  defaultVariants: {
    floatingInPage: false,
  },
});

type PageHeadingButtonVariants = VariantProps<typeof pageHeadingButtonVariants>;
export type PageHeadingButtonProps = React.ComponentPropsWithoutRef<typeof Button> & PageHeadingButtonVariants;

export const PageHeadingButton = React.forwardRef<React.ElementRef<typeof Button>, PageHeadingButtonProps>(({ children, className, floatingInPage, ...props }, ref) => {
  return (
    <Button ref={ref} className={cn(pageHeadingButtonVariants({ floatingInPage }), className)} type="button" {...props}>
      {children}
    </Button>
  );
});
PageHeadingButton.displayName = 'PageHeadingButton';

const pageHeadingBackButtonVariants = cva('gap-2 mb-4', {
  variants: {
    fixedInLayout: {
      true: '-md:fixed -md:top-2 -md:left-4 -md:z-30 -md:size-10 -md:mb-0 -md:p-0 -md:bg-transparent -md:text-foreground',
      false: '',
    },
  },
  defaultVariants: {
    fixedInLayout: false,
  },
});

type PageHeadingBackButtonVariants = VariantProps<typeof pageHeadingBackButtonVariants>;
export type PageHeadingBackButtonProps = Omit<React.ComponentPropsWithoutRef<typeof Button>, 'children'> & PageHeadingButtonVariants & PageHeadingBackButtonVariants;

export const PageHeadingBackButton = React.forwardRef<React.ElementRef<typeof Button>, PageHeadingBackButtonProps>(({ className, fixedInLayout, ...props }, ref) => {
  const { t } = useTranslation();

  return (
    <PageHeadingButton ref={ref} className={cn(pageHeadingBackButtonVariants({ fixedInLayout }), className)} type="button" {...props}>
      {fixedInLayout ? <ChevronLeft className="size-6 -md:block hidden" /> : null}
      <ArrowLeftIcon className={cn('size-4', fixedInLayout ? '-md:hidden' : '')} />
      <span className={cn(fixedInLayout ? '-md:hidden' : '')}>{t('fundamentals.back')}</span>
    </PageHeadingButton>
  );
});
PageHeadingBackButton.displayName = 'PageHeadingBackButton';
